/**
 * StatusUpdateHistoryModal
 *
 * Read-only modal that shows the last N weekly status updates for a single
 * brand, with the FULL details of each entry (TL;DR, vibe, output, top
 * performer, wins, blockers, next focus, strategic note).
 *
 * Triggered from:
 *   - the Status column in Grid / Board / Timeline views
 *   - the "View history" link in the brand drawer
 *
 * Each entry is collapsible — by default the most recent one is expanded so
 * the user lands on the freshest read; older entries fold to a one-line preview.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { useStatusUpdateHistory } from '@/hooks/usePortfolio';
import { useTimezone } from '@/contexts/TimezoneContext';
import type { PortfolioClient } from '@/data/portfolio.types';
import { useTheme } from '@/hooks/useTheme';
import { getContrastTextColor } from '@/utils/colors';
import { getStackZIndex, useModalStackDepth } from '@/contexts/ModalStackContext';
import { formatPeriodLabel, isoToYmd } from '@/utils/statusUpdatePeriod';
import { avatarTextClass } from '@/utils/avatarContrast';
import { statusPillLabel } from '@/utils/portfolioLabels';
import { lockBodyScroll } from '@/hooks/useBodyScrollLock';

interface StatusUpdateHistoryModalProps {
  client: PortfolioClient | null;
  onClose: () => void;
  /** Optional: triggered when the user clicks "Write update" — usually opens the compose modal */
  onComposeNew?: () => void;
}

// ── Pill + vibe display config (lifted from StatusUpdateComposeModal) ────────

const PILL_DISPLAY: Record<
  string,
  { emoji: string; bg: string; text: string }
> = {
  ON_TRACK:  { emoji: '🟢', bg: 'bg-primary-50', text: 'text-primary-700' },
  WATCH:     { emoji: '🟡', bg: 'bg-peach/30',   text: 'text-coral-700'   },
  AT_RISK:   { emoji: '🟠', bg: 'bg-coral-50',   text: 'text-coral-700'   },
  OFF_TRACK: { emoji: '🔴', bg: 'bg-coral-50',   text: 'text-coral-700'   },
  ON_HOLD:   { emoji: '⏸️', bg: 'bg-gray-50',    text: 'text-sage-gray'   },
  PAUSED:    { emoji: '⏯️', bg: 'bg-gray-50',    text: 'text-gray-500'    },
  COMPLETE:  { emoji: '✅', bg: 'bg-primary-25', text: 'text-primary-800' },
};

const VIBE_EMOJI: Record<string, string> = {
  STRONG: '😀',
  NEUTRAL: '😐',
  TENSE: '😬',
  ON_FIRE: '🔥',
};

// ── Brand header avatar — uses logo, custom icon+color, or first-letter ──────

const BrandHeaderAvatar = ({ client }: { client: PortfolioClient }) => {
  // Prefer uploaded logo image
  if (client.logoUrl) {
    return (
      <img
        src={client.logoUrl}
        alt={client.name}
        className="w-11 h-11 rounded-xl object-cover shrink-0"
      />
    );
  }
  // Then a custom icon + color combo (matches the rail / drawer pattern)
  if (client.icon) {
    const bgStyle = client.customColor ? { backgroundColor: client.customColor } : undefined;
    const contrastClass = client.customColor ? getContrastTextColor(client.customColor) : 'text-white';
    return (
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
          client.customColor ? '' : 'bg-primary-500'
        }`}
        style={bgStyle}
      >
        <i className={`hm hm-${client.icon} ${contrastClass}`} style={{ fontSize: 20 }} aria-hidden="true" />
      </div>
    );
  }
  // Fallback: first letter on the auto-assigned palette colour
  return (
    <div
      className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold ${avatarTextClass(client.avatarColor)} text-base shrink-0 ${client.avatarColor}`}
      aria-hidden="true"
    >
      {client.avatarInitial}
    </div>
  );
};

// ── Component ────────────────────────────────────────────────────────────────

export const StatusUpdateHistoryModal = ({
  client,
  onClose,
  onComposeNew,
}: StatusUpdateHistoryModalProps) => {
  const { t } = useTranslation('agency');
  const { formatRelativeDate } = useTimezone();
  // useTheme is lazy — pull whitelabel branding context (not used here yet,
  // but reserved if we later pass through agency-wide brand colours)
  useTheme();
  const isOpen = client !== null;

  // Stack-aware z-index so this modal sits above any modal it was opened from
  // (e.g. the StatusUpdateComposeModal launches this via the History icon).
  const stackDepth = useModalStackDepth(isOpen);
  const { backdrop: backdropZ, content: contentZ } = getStackZIndex(stackDepth);

  const { data: history = [], isLoading } = useStatusUpdateHistory(
    isOpen ? client.id : null,
    24, // up to ~6 months
  );

  // ESC + body-scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    document.addEventListener('keydown', handler);
    const unlockScroll = lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', handler);
      unlockScroll();
    };
  }, [isOpen, onClose]);

  if (!isOpen || !client) return null;

  return (
    <div
      className="fixed left-0 right-0 top-0 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:inset-0 flex items-end sm:items-center justify-center"
      style={{ zIndex: backdropZ }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="status-history-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        style={{ zIndex: contentZ }}
        className="
          relative bg-white flex flex-col shadow-2xl
          w-full h-full rounded-t-2xl rounded-b-none
          sm:h-auto sm:max-h-[90vh] sm:w-[calc(100vw-2rem)] sm:max-w-2xl sm:mx-0 sm:rounded-2xl
          overflow-hidden
        "
      >
        {/* Mobile drag handle */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" aria-hidden="true" />
        </div>

        {/* Sticky header — brand-branded */}
        <div className="shrink-0 border-b border-gray-900/10 px-5 py-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-medium mb-2">
            {t('status_history_overline', { defaultValue: 'Status history' })}
          </p>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <BrandHeaderAvatar client={client} />
              <div className="min-w-0">
                <h2
                  id="status-history-title"
                  className="font-wagner text-xl leading-tight tracking-tight text-gray-900 truncate"
                >
                  {client.name}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {history.length === 0
                    ? t('status_history_subtitle_empty', { defaultValue: 'No updates yet' })
                    : t('status_history_subtitle_count', {
                        count: history.length,
                        defaultValue: `${history.length} update${history.length === 1 ? '' : 's'}`,
                      })}
                  {client.ownerName && (
                    <>
                      <span className="mx-1.5 text-gray-300" aria-hidden="true">·</span>
                      {t('status_history_lead', {
                        name: client.ownerName,
                        defaultValue: `Lead: ${client.ownerName}`,
                      })}
                    </>
                  )}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors outline-none border border-transparent focus-visible:border-primary-600 shrink-0"
              onClick={onClose}
              aria-label={t('status_history_close', { defaultValue: 'Close' })}
            >
              <i className="hm hm-cross" style={{ fontSize: 15 }} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {isLoading && (
            <p className="text-xs text-gray-400 italic px-1 py-2">
              {t('status_history_loading', { defaultValue: 'Loading status updates…' })}
            </p>
          )}
          {!isLoading && history.length === 0 && (
            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <p className="text-sm text-gray-500 mb-3">
                {t('status_history_empty', {
                  defaultValue: 'No status updates have been written for this brand yet.',
                })}
              </p>
              {onComposeNew && (
                <Button variant="primary" size="sm" onClick={onComposeNew}>
                  <i className="hm hm-pen-pencil mr-1.5" style={{ fontSize: 13 }} aria-hidden="true" />
                  {t('status_history_compose_first', { defaultValue: 'Write the first update' })}
                </Button>
              )}
            </div>
          )}

          <div className="space-y-3">
            {history.map((row) => {
              const pill = PILL_DISPLAY[row.colorPill] ?? PILL_DISPLAY.WATCH;
              const vibe = row.vibe ? (VIBE_EMOJI[row.vibe] ?? '😐') : null;
              const narrative = [
                {
                  key: 'wins',
                  label: t('portfolio_drawer_status_wins', { defaultValue: 'Wins' }),
                  value: row.wins,
                  accent: 'text-primary-700',
                },
                {
                  key: 'blockers',
                  label: t('portfolio_drawer_status_blockers', { defaultValue: 'Blockers' }),
                  value: row.blockers,
                  accent: 'text-coral-700',
                },
                {
                  key: 'nextFocus',
                  label: t('portfolio_drawer_status_next_focus', { defaultValue: 'Next focus' }),
                  value: row.nextFocus,
                  accent: 'text-gray-700',
                },
                {
                  key: 'strategicNote',
                  label: t('portfolio_drawer_status_strategic', { defaultValue: 'Strategic note' }),
                  value: row.strategicNote,
                  accent: 'text-gray-700',
                },
              ];
              const populatedNarrative = narrative.filter((n) => n.value && n.value.trim());
              const hasOutput = row.postsPublished > 0 || row.postsPlanned > 0;
              const hasExtras =
                populatedNarrative.length > 0 || row.topPost !== null || hasOutput;

              return (
                <div
                  key={row.id}
                  className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm"
                >
                  {/* Header: pill + week */}
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span
                      className={`text-[11px] px-2 py-0.5 rounded-full font-semibold leading-none ${pill.bg} ${pill.text}`}
                    >
                      {pill.emoji} {statusPillLabel(t, PILL_DISPLAY[row.colorPill] ? row.colorPill : 'WATCH')}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium">
                      {formatPeriodLabel(
                        isoToYmd(row.weekStart),
                        // Defensive fallback for rows that predate the periodEnd column
                        isoToYmd(row.periodEnd || row.weekStart),
                        t('status_compose_week_of', { defaultValue: 'Week of' }),
                        (week) => t('status_compose_week_number', { week, defaultValue: `KW ${week}` }),
                      )}
                    </span>
                  </div>

                  {/* Vibe + TL;DR */}
                  <div className="flex items-start gap-3">
                    {vibe && (
                      <span className="text-2xl leading-none shrink-0 mt-0.5" aria-hidden="true">
                        {vibe}
                      </span>
                    )}
                    <p className="flex-1 text-sm text-gray-800 leading-snug font-medium min-w-0">
                      {row.tldr}
                    </p>
                  </div>

                  {/* Output chip + meta */}
                  <div className="flex items-center gap-2 flex-wrap mt-3">
                    {hasOutput && (
                      <span className="inline-flex items-baseline gap-1 text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
                        <span className="text-sm font-bold tabular-nums">{row.postsPublished}</span>
                        <span className="text-gray-400">/</span>
                        <span className="text-sm font-bold tabular-nums">{row.postsPlanned}</span>
                        <span className="ml-0.5 text-[10px] uppercase tracking-wide text-gray-500">
                          {t('status_history_output_label', { defaultValue: 'posts' })}
                        </span>
                      </span>
                    )}
                    <span className="text-[11px] text-gray-400">
                      {row.authorName}
                      <span aria-hidden="true" className="mx-1.5 text-gray-300">·</span>
                      {formatRelativeDate(row.createdAt)}
                    </span>
                  </div>

                  {/* Always-visible details: top performer + narrative blocks */}
                  {hasExtras && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                      {/* Top performer */}
                      {row.topPost && (
                        <div className="flex items-center gap-2.5 p-2 rounded-lg bg-gray-50">
                          {row.topPost.thumbnailUrl ? (
                            <img
                              src={row.topPost.thumbnailUrl}
                              alt=""
                              className="w-10 h-10 rounded-md object-cover shrink-0"
                              aria-hidden="true"
                            />
                          ) : (
                            <div
                              className="w-10 h-10 rounded-md bg-gradient-to-br from-primary-400 to-primary-600 shrink-0 flex items-center justify-center"
                              aria-hidden="true"
                            >
                              <i className="hm hm-star text-white" style={{ fontSize: 16 }} aria-hidden="true" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium leading-none mb-0.5">
                              {t('portfolio_drawer_status_top_performer', { defaultValue: 'Top performer' })}
                            </p>
                            <p className="text-xs text-gray-700 leading-snug line-clamp-2">
                              {row.topPost.caption}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Narrative blocks — wins / blockers / next focus / strategic note */}
                      {populatedNarrative.map((n) => (
                        <div key={n.key}>
                          <p
                            className={`text-[10px] uppercase tracking-[0.15em] font-semibold mb-0.5 ${n.accent}`}
                          >
                            {n.label}
                          </p>
                          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                            {n.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sticky footer */}
        <div className="shrink-0 border-t border-gray-100 bg-white px-5 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-2 justify-end">
            <CancelButton onClick={onClose} tooltip={t('status_history_close', { defaultValue: 'Close' })} />
            {onComposeNew && (
              <Button variant="primary" size="md" onClick={onComposeNew}>
                <i className="hm hm-pen-pencil mr-1.5" style={{ fontSize: 14 }} aria-hidden="true" />
                {t('status_history_write_new', { defaultValue: 'Write new update' })}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdateHistoryModal;
